import { Flex, FormGroup, FormLabel, Grid } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikError, FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { ILocation } from "@/data/locations";
import { initialMapPhone } from "@/data/phones";
import { FormikForm } from "@/formikComponents";
import { FormikTypeaheadLocationHints } from "@/views/components/Formik";
import { IPhoneFormValues, PhoneForm } from "@/views/components/Forms";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";

export const POSITION_MAX_CHARACTERS = 100;
export const NAME_MAX_CHARACTERS = 100;
export const EMAIL_MAX_CHARACTERS = 100;

export const validationGeneralSectionSchema = Yup.object().shape({
  firstName: Yup.string().max(NAME_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH).required(VALIDATION_TYPES.REQUIRED),
  lastName: Yup.string().max(NAME_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH).required(VALIDATION_TYPES.REQUIRED),
  position: Yup.object().shape({
    name: Yup.string().max(POSITION_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH),
  }),
  phone: Yup.object()
    .shape(
      {
        phoneNumber: Yup.string()
          .nullable(true)
          .test({
            message: VALIDATION_TYPES.NUMBERS,
            test: (phone: any) => !phone || !isNaN(phone),
          }),
        phoneCode: Yup.string()
          .nullable(true)
          .when("phoneNumber", {
            is: (val: any) => !!val,
            then: Yup.string().required(VALIDATION_TYPES.REQUIRED),
            otherwise: Yup.string().nullable(true),
          }),
      },
      [["phoneCode", "phoneNumber"]],
    )
    .nullable(true),
});

export interface IGeneralFormSectionValues extends IPhoneFormValues {
  firstName: string;
  lastName: string;
  email: string;
  location: ILocation | null;
  position: { name: string };
}

interface IGeneralFormSectionProps {
  form: FormikProps<IGeneralFormSectionValues>;
}

interface IGeneralSectionProps extends IGeneralFormSectionProps, IEditableSectionRenderProps {
  source: IConsultantProfile;
}

export const GeneralSectionForm: React.FC<IGeneralSectionProps> = memo(({ editableProps, form, source }) => {
  const { t } = useTranslation("session");
  const isPrivateProfile = !source.companyRole;

  useEditableSubmit(form, editableProps);

  return (
    <FormikForm>
      <Flex flexDirection="column" gap={["sm", null, "xs"]}>
        <Grid columns={["1fr", null, "1fr 2fr"]} gap={["xs", null, "sm"]}>
          <FormLabel label={t("CONSULTANT_PROFILES.DETAIL.FIRST_NAME.LABEL")} required />
          <FormGroup
            error={
              <FormikError
                name="firstName"
                overrideMessages={{
                  [VALIDATION_TYPES.MAX_LENGTH]: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                    value: NAME_MAX_CHARACTERS,
                  }),
                }}
              />
            }>
            <FormikTextInput type="text" name="firstName" value={form.values.firstName} required />
          </FormGroup>
        </Grid>
        <Grid columns={["1fr", null, "1fr 2fr"]} gap={["xs", null, "sm"]}>
          <FormLabel label={t("CONSULTANT_PROFILES.DETAIL.LAST_NAME.LABEL")} required />
          <FormGroup
            error={
              <FormikError
                name="lastName"
                overrideMessages={{
                  [VALIDATION_TYPES.MAX_LENGTH]: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                    value: NAME_MAX_CHARACTERS,
                  }),
                }}
              />
            }>
            <FormikTextInput type="text" name="lastName" value={form.values.lastName} required />
          </FormGroup>
        </Grid>
        {!isPrivateProfile && (
          <Grid columns={["1fr", null, "1fr 2fr"]} gap={["xs", null, "sm"]}>
            <FormLabel label={t("CONSULTANT_PROFILES.DETAIL.COMPANY_POSITION.LABEL")} />
            <FormGroup
              error={
                <FormikError
                  name="position.name"
                  overrideMessages={{
                    [VALIDATION_TYPES.MAX_LENGTH]: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                      value: POSITION_MAX_CHARACTERS,
                    }),
                  }}
                />
              }>
              <FormikTextInput
                placeholder={t("CONSULTANT_PROFILES.DETAIL.COMPANY_POSITION.PLACEHOLDER")}
                type="text"
                name="position.name"
                value={form.values.position.name}
              />
            </FormGroup>
          </Grid>
        )}
        <Grid columns={["1fr", null, "1fr 2fr"]} gap={["xs", null, "sm"]}>
          <FormLabel label={t("CONSULTANT_PROFILES.DETAIL.LOCATION.LABEL")} required={isPrivateProfile} />
          <FormGroup error={<FormikError name="location" />}>
            <FormikTypeaheadLocationHints
              name="location"
              testName="location"
              size="md"
              value={form.values.location}
              valueKey="name"
              labelKey="name"
            />
          </FormGroup>
        </Grid>
        <Grid columns={["1fr", null, "1fr 2fr"]} gap={["xs", null, "sm"]}>
          <FormLabel label={t("CONSULTANT_PROFILES.DETAIL.PHONE.SECTION_HEADER")} />
          <FormGroup>
            <PhoneForm withLabels={false} form={form} location={form.values.location} />
          </FormGroup>
        </Grid>
      </Flex>
    </FormikForm>
  );
});

interface IGeneralFormikProps extends IProfileFormikProps<IGeneralFormSectionValues>, IGeneralSectionProps {}

export const GeneralSectionFormik: React.FunctionComponent<IGeneralFormikProps> = React.memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => {
    const isPrivateProfile = !rest.source.companyRole;

    const locationSchema = Yup.object().shape({
      location: Yup.object().required(VALIDATION_TYPES.REQUIRED).nullable(true),
    });

    const finalValidationSchema = !isPrivateProfile ? validationSchema : validationSchema.concat(locationSchema);

    return (
      <Formik
        initialValues={{
          ...initialValues,
          phone: initialMapPhone(initialValues.phone),
        }}
        validationSchema={finalValidationSchema}
        onSubmit={onSubmit}>
        {(form: FormikProps<IGeneralFormSectionValues>) => <GeneralSectionForm {...rest} form={form} />}
      </Formik>
    );
  },
);

export const CV_MESSENGER_CONTEXT = "profile-cv-form";
export const GeneralSectionEdit = profilePartialUpdaterBuilder(GeneralSectionFormik, {
  validationSchema: validationGeneralSectionSchema,
  messengerContext: CV_MESSENGER_CONTEXT,
});
